import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

export const CARD_DETAILS_EVENTS = {
  SUCCESS: 'card:details:success',
  FAILED: 'card:details:failed',
  SHOW: 'card:details:show',
};

export const CARD_DETAILS_TIMEOUT_MS = 30000;

const PAGE_EVENTS = [CARD_DETAILS_EVENTS.SUCCESS, CARD_DETAILS_EVENTS.FAILED];

export const readCardDetailsMessage = (event, { origin, source }) => {
  if (event.origin !== origin || event.source !== source) {
    return null;
  }

  let payload = event.data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch (error) {
      return null;
    }
  }

  return PAGE_EVENTS.includes(payload?.event) ? payload : null;
};

const HostedCardDetails = ({ url, onFailed }) => {
  const { t: translate } = useTranslation();
  const frameRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);
  const pageOrigin = useMemo(() => new URL(url).origin, [url]);

  const showBackOfCard = useCallback(() => {
    frameRef.current?.contentWindow?.postMessage(
      { event: CARD_DETAILS_EVENTS.SHOW, data: { side: 'back' } },
      pageOrigin
    );
  }, [pageOrigin]);

  useEffect(() => {
    const handleMessage = (event) => {
      const message = readCardDetailsMessage(event, {
        origin: pageOrigin,
        source: frameRef.current?.contentWindow,
      });
      if (!message) {
        return;
      }
      if (message.event === CARD_DETAILS_EVENTS.SUCCESS) {
        setIsRendered(true);
        showBackOfCard();
        return;
      }
      onFailed(message.data?.error);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pageOrigin, onFailed, showBackOfCard]);

  useEffect(() => {
    if (isRendered) {
      return undefined;
    }
    const timer = setTimeout(() => onFailed('CARD_DETAILS_TIMEOUT'), CARD_DETAILS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isRendered, onFailed]);

  return (
    <div className="py-4">
      <iframe
        ref={frameRef}
        className="w-100 rounded"
        style={{ aspectRatio: '370 / 218', border: 0 }}
        title={translate('card_connect_financial_details_title')}
        src={url}
        sandbox="allow-scripts allow-same-origin"
        allow="clipboard-write"
        onLoad={showBackOfCard}
      />
    </div>
  );
};

export default HostedCardDetails;

HostedCardDetails.propTypes = {
  url: PropTypes.string.isRequired,
  onFailed: PropTypes.func.isRequired,
};
